/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { EnvelopeClosedIcon, MobileIcon, Text, UnstyledButton } from '@/shared/ui';
import { WhatsAppIcon } from '@/shared/ui/components/WhatsAppQRCode.parts';
import { SectionCard } from '@/features/property-management/components/PropertyDetailView/SectionCard';
import { ClientsService } from '@services/requests/housien-services/ClientsService';

export interface DealClientCardProps {
  agencyId: string;
  clientId?: string;
}

/** One way to reach the client. */
interface Channel {
  id: string;
  icon: ReactNode;
  value: string;
  href: string;
}

/**
 * The client's reachable channels, skipping the ones with no value.
 *
 * @param client - The client record, when it has loaded.
 * @returns The channels, in the order a broker tries them.
 */
function channelsOf(client: Record<string, any> | undefined): Channel[] {
  if (!client) return [];
  // The record keeps its reachable channels as a typed list under
  // `assignment.contacts`, not as `phone`/`email` fields. Reading the
  // fields left this card saying the client had no channels while the
  // client page, which reads the list, showed three.
  const contacts: Array<{ type?: string; value?: string }> =
    client.assignment?.contacts ?? client.contacts ?? [];
  const valueOf = (type: string) =>
    contacts.find((c) => c.type === type)?.value
    ?? (type === 'whatsapp' ? contacts.find((c) => c.type === 'phone')?.value : undefined);

  const whatsapp = valueOf('whatsapp');
  const phone = valueOf('phone') ?? client.phone;
  const email = valueOf('email') ?? client.email;

  const channels: Channel[] = [];
  if (whatsapp) {
    channels.push({
      id: 'whatsapp',
      icon: <WhatsAppIcon className="h-[14px] w-[14px]" />,
      value: whatsapp,
      href: `https://wa.me/${whatsapp.replace(/\D/g, '')}`,
    });
  }
  if (phone) {
    channels.push({
      id: 'phone',
      icon: <MobileIcon width={14} height={14} />,
      value: phone,
      href: `tel:${phone.replace(/\D/g, '')}`,
    });
  }
  if (email) {
    channels.push({
      id: 'email',
      icon: <EnvelopeClosedIcon width={14} height={14} />,
      value: email,
      href: `mailto:${email}`,
    });
  }
  return channels;
}

/**
 * The client's contact channels beside the attendance, with the way
 * through to their full record.
 *
 * The attendance names the client but never says how to reach them, so
 * the one thing a broker leaves this screen to do — call or message —
 * used to cost a trip to the client page and back.
 *
 * @param props - The agency and the attendance's client.
 * @returns The card, or nothing when the attendance has no client.
 */
export function DealClientCard({ agencyId, clientId }: DealClientCardProps) {
  const { t } = useTranslation('features/deals');
  const { data } = useQuery({
    queryKey: ['clients', agencyId, 'record', clientId] as const,
    queryFn: () => ClientsService.getAgencyClient(agencyId, clientId as string),
    enabled: Boolean(agencyId && clientId),
  });

  if (!clientId) return null;
  const channels = channelsOf(data as Record<string, any> | undefined);

  return (
    <SectionCard
      data-testid="deal-client-card"
      title={t('detail.client.title')}
      titleAccessory={(
        <Link
          to={`/app/agencies/${agencyId}/clients/${clientId}`}
          data-testid="deal-client-open"
          className="whitespace-nowrap text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-(--seed-base) no-underline hover:opacity-80"
        >
          {t('detail.client.openRecord')}
        </Link>
      )}
    >
      {channels.length === 0 ? (
        <Text className="m-0 text-[length:var(--text-meta)] text-(--text-tertiary-soft)">
          {t('detail.client.noChannels')}
        </Text>
      ) : (
        <div className="flex flex-col gap-(--space-2)">
          {channels.map((channel) => (
            <UnstyledButton
              key={channel.id}
              type="button"
              data-testid={`deal-client-${channel.id}`}
              onClick={() => window.open(channel.href, '_blank', 'noopener,noreferrer')}
              className="box-border flex w-full items-center gap-(--space-2-5) rounded-(--radius-md) border border-solid border-(--border-subtle) bg-(--surface-primary) px-(--space-2-5) py-(--space-2) text-left cursor-pointer hover:bg-(--surface-hover)"
            >
              <Text component="span" className="flex flex-none text-(--seed-base)">{channel.icon}</Text>
              <Text
                component="span"
                className="min-w-0 flex-1 truncate text-[length:var(--text-body-sm)] text-(--text-primary)"
              >
                {channel.value}
              </Text>
            </UnstyledButton>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
